import DOMPurify from "dompurify";

function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function buildHighlightTerms(highlight) {
    const keyword = (highlight?.keyword || "").trim();
    const extraKeywords = Array.isArray(highlight?.keywords) ? highlight.keywords : [];

    const rawTerms = [keyword, ...extraKeywords]
        .map(t => String(t || "").trim())
        .filter(Boolean);

    if (!rawTerms.length) return [];

    const match = highlight?.match || "all";
    const seen = new Set();
    const addUnique = (t, out) => {
        const key = t.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(t);
    };

    if (match === "exact") {
        const out = [];
        rawTerms.forEach((t) => addUnique(t, out));
        return out;
    }

    // split into tokens for "all"/"any"
    const tokens = [];
    rawTerms
        .flatMap(t => t.split(/\s+/))
        .map(t => t.trim())
        .filter(Boolean)
        .filter(t => t.length >= 2)
        .forEach((t) => addUnique(t, tokens));

    return tokens;
}

export function highlightHtmlString(html, terms) {
    if (!html || !terms?.length) return html;

    const sorted = [...terms].sort((a, b) => b.length - a.length);
    const re = new RegExp(`(${sorted.map(escapeRegExp).join("|")})`, "gi");

    const doc = new DOMParser().parseFromString(html, "text/html");
    const root = doc.body;

    const walker = doc.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode(node) {
                const text = node.nodeValue || "";
                if (!text.trim()) return NodeFilter.FILTER_REJECT;

                const parent = node.parentElement;
                if (!parent) return NodeFilter.FILTER_REJECT;

                const tag = parent.tagName?.toLowerCase();
                if (tag === "script" || tag === "style") return NodeFilter.FILTER_REJECT;
                if (parent.closest("mark.kw-highlight")) return NodeFilter.FILTER_REJECT;

                return NodeFilter.FILTER_ACCEPT;
            }
        }
    );

    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const node of textNodes) {
        const text = node.nodeValue || "";
        if (!re.test(text)) { re.lastIndex = 0; continue; }
        re.lastIndex = 0;

        const frag = doc.createDocumentFragment();
        let last = 0;

        text.replace(re, (m, _g, offset) => {
            if (offset > last) frag.appendChild(doc.createTextNode(text.slice(last, offset)));

            const mark = doc.createElement("mark");
            mark.className = "kw-highlight";
            mark.textContent = m;
            frag.appendChild(mark);

            last = offset + m.length;
            return m;
        });

        if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));

        node.parentNode.replaceChild(frag, node);
    }

    return root.innerHTML;
}

export function sectionContainsKeyword(html, terms) {
    if (!html || !terms?.length) return false;

    const sorted = [...terms].sort((a, b) => b.length - a.length);
    const re = new RegExp(`(${sorted.map(escapeRegExp).join("|")})`, "gi");

    // Parse the HTML and extract text content
    const doc = new DOMParser().parseFromString(html, "text/html");
    const textContent = doc.body.textContent || "";

    return re.test(textContent);
}

export function sanitizeAndHighlight(html, highlight, domPurifyConfig = {}) {
    const terms = buildHighlightTerms(highlight);
    const clean = DOMPurify.sanitize(html || "", {
        ...domPurifyConfig,
        ADD_TAGS: ["mark", "span"],
        ADD_ATTR: ["class", "style", "data-term", "data-definition"],
    });

    const highlighted = highlightHtmlString(clean, terms);

    const cleanAgain = DOMPurify.sanitize(highlighted, {
        ...domPurifyConfig,
        ADD_TAGS: ["mark", "span"],
        ADD_ATTR: ["class", "style", "data-term", "data-definition"],
    });

    return { __html: cleanAgain };
}
